// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { strongEntries } from 'helpers/strongEntries';
import { useSpoolAssignment } from 'modules/Shop/Spooling/hooks/useSpoolAssignment';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

export const filterNoneKey = `_none$`;

export type FilterNoneKey = typeof filterNoneKey;

export type FilterKey = Exclude<keyof RenderableSpool, 'name' | 'objects'>;

export type SpoolFilter = Partial<Record<FilterKey, (string | FilterNoneKey)[]>>;

const checkIfSpoolPassesFilter = (spool: RenderableSpool, searchPhrase: string, spoolFilter: SpoolFilter) =>
  Object.values(spool).some(
    (v) => typeof v === 'string' && v.toLowerCase().includes(searchPhrase.toLowerCase().trim()),
  ) &&
  strongEntries(spoolFilter)
    .filter(([__, values]) => !!values?.length)
    .every(
      ([key, values]) =>
        (spool[key] && values?.includes(spool[key])) || (!spool[key] && values?.includes(filterNoneKey)),
    );

export const filterSpools = <T>(
  spools: readonly T[],
  getRenderableSpool: (spool: T) => RenderableSpool,
  searchPhrase: string,
  spoolFilter: SpoolFilter,
) => spools.filter((spoolData) => checkIfSpoolPassesFilter(getRenderableSpool(spoolData), searchPhrase, spoolFilter));

/**
 * Applies the search phrase and filter to the requested spools, unmapped spools, and mapped spools.
 */
export const useSpoolFilters = () => {
  const { spoolRequests } = useSpoolRequestsViewerState();

  const { unassignedSpools: unfilteredUnassigned, assignedSpools: unfilteredAssigned } = useSpoolAssignment();

  /**
   * Filter the spools by the search phrase and filter.
   * For unmapped spools, this is pretty straightforward.
   * Mapped spools are more complex since they're grouped by work order. So we make a filtered version of each group,
   * then we filter out any groups where no spools remain.
   */
  const [searchPhrase, setSearchPhrase] = useState('');
  const [spoolFilter, setSpoolFilter] = useState<SpoolFilter>({});

  const filteredRequestedSpools = useMemo(
    () => filterSpools(spoolRequests ?? [], ([_, spool]) => spool, searchPhrase, spoolFilter),
    [spoolRequests, searchPhrase, spoolFilter],
  );

  const filteredUnassignedSpools = useMemo(
    () =>
      filterSpools(unfilteredUnassigned, (spool) => spool, searchPhrase, spoolFilter).toSorted((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [unfilteredUnassigned, searchPhrase, spoolFilter],
  );
  const filteredAssignedSpools = useMemo(
    () =>
      unfilteredAssigned
        .map(([workOrderId, mappedSpoolGroup]): (typeof unfilteredAssigned)[number] => [
          workOrderId,
          filterSpools(mappedSpoolGroup, (spool) => spool, searchPhrase, spoolFilter).toSorted((a, b) =>
            a.name.localeCompare(b.name),
          ),
        ])
        .filter(([, mappedSpoolGroup]) => mappedSpoolGroup.length > 0),
    [unfilteredAssigned, searchPhrase, spoolFilter],
  );

  return {
    searchPhrase,
    setSearchPhrase,
    spoolFilter,
    setSpoolFilter,
    filteredRequestedSpools,
    filteredUnassignedSpools,
    filteredAssignedSpools,
  };
};
